import type { RecordView } from '@kit/sync/types/record-view';
import { toast } from 'sonner';
import { createNote } from '@/entities/note/api/create-note';
import { deleteNote } from '@/entities/note/api/delete-note';
import { saveNote } from '@/entities/note/api/save-note';
import type { Note } from '@/entities/note/schemas/note';
import { usePersonName } from '@/entities/session/hooks/use-person-name';
import { RecordMeta } from '@/entities/shared/record/record-meta';
import { EditorSheet } from '@/shared/components/editor-sheet/editor-sheet';
import { parseTags } from '@/shared/components/form/parse-tags';
import { SwitchField } from '@/shared/components/form/switch-field';
import { TagsField } from '@/shared/components/form/tags-field';
import { TextField } from '@/shared/components/form/text-field';
import { TextareaField } from '@/shared/components/form/textarea-field';
import { useForm } from '@/shared/hooks/use-form';
import { useTranslation } from '@/shared/i18n/use-translation';

type NoteEditorProps = {
  readonly view: RecordView<Note> | undefined;
  readonly onClose: () => void;
};

export function NoteEditor({ view, onClose }: NoteEditorProps) {
  const { t } = useTranslation();
  const nameOf = usePersonName();
  const note = view?.data;
  const form = useForm({
    title: note?.title ?? '',
    body: note?.body ?? '',
    tags: (note?.tags ?? []).join(', '),
    pinned: note?.pinned ?? false,
  });

  async function save() {
    const saved = await form.submit(async function write() {
      const data: Partial<Note> = { ...note, ...form.values, tags: parseTags(form.values.tags) };
      if (view) await saveNote(view.id, data);
      else await createNote(data);
    });
    if (!saved) return;
    toast.success(t('Note saved'));
    onClose();
  }

  async function remove() {
    if (!view) return;
    await deleteNote(view.id);
    onClose();
  }

  return (
    <EditorSheet
      title={view ? view.data.title : t('New note')}
      saving={form.saving}
      onClose={onClose}
      onSubmit={save}
      onDelete={view ? remove : undefined}
    >
      <TextField label={t('Title')} required autoFocus={!view} {...form.field('title')} />
      <TextareaField label={t('Text')} rows={12} {...form.field('body')} />
      <TagsField label={t('Tags')} {...form.field('tags')} />
      <SwitchField
        label={t('Pin to the top')}
        checked={form.values.pinned}
        onChange={(pinned) => form.set('pinned', pinned)}
      />
      {view && <RecordMeta view={view} nameOf={nameOf} />}
    </EditorSheet>
  );
}
